import React, { ComponentProps, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { GridApi } from 'ag-grid-community'
import {
  EntityType,
  EntityTypeEnum,
  NegotiationActions,
  NegotiationDetail,
  NegotiationEvent,
  NegotiationPackageDetail,
  PlatformFeature,
  RfqType,
  SubscriptionIdentifier
} from 'api'
import { CenterPanel } from '../../../routesLayout'
import { DataGrid, GridAutoSizing, GridId } from 'view/components/datagrid'
import { shouldStartEdit } from 'view/routes/requests/shared/utils'
import { RepositoryActions } from 'store/repository'
import { createEntitySubscriptionSelector } from 'selectors/selectionSelectors'
import { GridActions } from 'store/grid'
import { curveGridOptions } from './CurveNegotiationsGridModel'
import {
  curveItemNegotiationsBySelectedOrderFundId,
  curveItemNegotiationsSelector,
  curveNegotiationPackagesSelector
} from './CurveNegotiationSelectors'
import { NegotiationFieldNames } from '../../LiveNegotiationsGrid/LiveGridColumnNames'
import { primitive, sliceFieldName, useParametricSelector } from 'utils'
import { IncomingNegotiationsHeroPanel } from '../IncomingNegotiationsHeroPanel'
import { SelectionActions } from 'store/selection'
import { incomingCurveNegotiationsColumnDefWithMappedHideStateSelector, pristineColDefSelector } from './CurveNegotiationGridSelectors'
import { FeatureToggle } from 'view/shell/FeatureToggle'
import { CurveGridSelectionProvider, useCurveGridSelectionContext } from './CurveGridSelectionContext'
import { CurveGridClipboardProcessor } from './CurveGridClipboardProcessor'
import { Locale, Translation } from 'locale'
import { ColumnCopySelector } from '../components/ColumnCopySelector'
import { SalesCoverFilterSwitch } from 'view/components/datagrid/toolbar/SalesCoverFilterSwitch'
import { QuotePricedNegotiationsButton } from '../components/QuotePricedNegotiationsButton/QuotePricedNegotiationsButton'
import { CurveGridDropzoneProvider } from './CurveGridDropzoneProvider'
import { generateExportFileName } from 'view/components/datagrid/gridUtils/utils'
import { useIsSnapshotLoaded, useUserPreference } from 'view/hooks'

const subscriptionDetailSelector = createEntitySubscriptionSelector(
  () => EntityTypeEnum.NEGOTIATION,
  () => SubscriptionIdentifier.INCOMING
)

export const CurveNegotiationsScreen: React.FC = () => {
  const dispatch = useDispatch()
  const userPreference = useUserPreference()
  const snapshotLoaded = useIsSnapshotLoaded(EntityTypeEnum.NEGOTIATION)
  const negotiationPackages = useSelector(curveNegotiationPackagesSelector) as NegotiationPackageDetail[]
  const curveNegotiations = useSelector(curveItemNegotiationsBySelectedOrderFundId)
  const subscriptionDetail = useSelector(subscriptionDetailSelector)
  const pristineColDefs = useSelector(pristineColDefSelector)
  const columnDefs = useParametricSelector(incomingCurveNegotiationsColumnDefWithMappedHideStateSelector, null)

  const [clipboardProcessor] = useState(() => new CurveGridClipboardProcessor({ copyPasteFormat: userPreference.copyPasteFormat }))

  const handleCellValueChanged = (_negotiationId: string, negotiationDetail: NegotiationDetail, _changedField: String) => {
    const { negotiation, availableEvents } = negotiationDetail
    if (availableEvents.includes(NegotiationEvent.LOCK) && !negotiation.userIdWithLock) {
      dispatch(NegotiationActions.lock(negotiation.negotiationId, false))
    }
  }

  const handleCellValueUpdated = (
    negotiationId: string,
    _negotiationDetail: NegotiationDetail,
    field: string,
    value: primitive,
    rawValue?: primitive,
    gridApi?: GridApi
  ) => {
    if ([NegotiationFieldNames.START_DATE_MODIFIED, NegotiationFieldNames.END_DATE_MODIFIED].includes(field)) {
      gridApi?.stopEditing()
      dispatch(SelectionActions.setGridRowInEdit(null, null))
    }
    if (field === NegotiationFieldNames.OTW_DURATION) {
      dispatch(RepositoryActions.updateMeta(EntityType.NEGOTIATION, negotiationId, field, value, { ownOrder: false }))
    } else {
      const fieldName = sliceFieldName(field, 1)
      dispatch(RepositoryActions.updateField(EntityType.NEGOTIATION, negotiationId, fieldName, value, rawValue, { ownOrder: false }))
    }
  }

  const shouldRedrawOnCellValueUpdate = (field: string, rowItem: NegotiationDetail) => {
    return (
      (field === NegotiationFieldNames.NOTIONAL_MODIFIED || field === NegotiationFieldNames.START_CONSIDERATION_MODIFIED) &&
      rowItem.negotiation.rfqType.negotiated === RfqType.INDICATIVE
    )
  }

  const shouldStopEditOnRowDataUpdate = (
    previousNegotiationDetail: NegotiationDetail | null,
    newNegotiationDetail: NegotiationDetail | null
  ) => previousNegotiationDetail?.negotiation?.status !== newNegotiationDetail?.negotiation?.status

  const onPageChanged = (pageNumber: number) => {
    if (subscriptionDetail) {
      dispatch(NegotiationActions.pageSubscribe(SubscriptionIdentifier.INCOMING, subscriptionDetail.filters, pageNumber))
    }
  }

  const handleAfterRestoreDefaultLayout = (gridApi: GridApi) => {
    const columnState = gridApi.getColumnState()
    gridApi.applyColumnState({
      state: columnState.map(colState => ({
        ...colState,
        hide: pristineColDefs.find(cd => cd.colId === colState.colId)?.hide ?? colState.hide
      }))
    })
    dispatch(GridActions.saveGridColumnCopySelections(GridId.CurvesGrid, 'default', []))
  }

  const gridApiRef = useRef<GridApi>()

  return (
    <CurveGridDropzoneProvider>
      <FeatureToggle feature={PlatformFeature.ENABLE_CURVE_COLLECTIONS}>
        <CenterPanel>
          <IncomingNegotiationsHeroPanel
            currentNegotiations={curveNegotiations}
            headerLabel={Locale.translate(Translation.TC_HERO_CURVES)}
            tearoutUrl="/quote/tearout/rfqs/curves"
          />
          <CurveGridSelectionProvider negotiationPackages={negotiationPackages} gridApi={gridApiRef.current}>
            <DataGridWithOnSelectionChanged
              disableGrouping={true}
              gridId={GridId.CurvesGrid}
              gridApiRef={gridApiRef}
              gridClass="live-grid live-grid--pivoted-view"
              gridOptions={curveGridOptions}
              gridColumnAutoSizing={GridAutoSizing.NONE}
              columnDefs={negotiationPackages.length > 0 ? columnDefs : []}
              rowData={negotiationPackages}
              trackGridRowInEdit={true}
              canEditRow={shouldStartEdit}
              shouldRedrawOnCellValueUpdate={shouldRedrawOnCellValueUpdate}
              shouldStopEditOnRowDataUpdate={shouldStopEditOnRowDataUpdate}
              subscriptionDetail={subscriptionDetail}
              onPageChanged={onPageChanged}
              onCellValueChanged={handleCellValueChanged}
              onCellValueUpdated={handleCellValueUpdated}
              showLoader={!snapshotLoaded}
              clipboardProcessor={clipboardProcessor}
              customToolbarSwitches={[SalesCoverFilterSwitch]}
              GroupByToggles={() => null}
              customToolbarComponents={[
                () => (
                  <QuotePricedNegotiationsButton
                    negotiationsSelector={curveItemNegotiationsSelector}
                    onClick={() => {
                      gridApiRef.current?.stopEditing()
                      dispatch(SelectionActions.setGridRowInEdit(null, null))
                    }}
                  />
                ),
                ColumnCopySelector
              ]}
              onAfterRestoreDefaultLayout={handleAfterRestoreDefaultLayout}
              exportFileName={generateExportFileName('Curves')}
            />
          </CurveGridSelectionProvider>
        </CenterPanel>
      </FeatureToggle>
    </CurveGridDropzoneProvider>
  )
}

const DataGridWithOnSelectionChanged: React.FC<ComponentProps<typeof DataGrid>> = props => {
  const { onGridSelectionChanged } = useCurveGridSelectionContext()
  return <DataGrid {...props} onSelectionChanged={onGridSelectionChanged} />
}
